<script lang="ts">
    import { _ } from 'svelte-i18n';
    import { blur } from 'svelte/transition';
    import { onMount } from 'svelte';
    import { inview } from 'svelte-inview/dist/index';
    import { get } from 'svelte/store';
    import { captureMessage } from '../../utilities/Sentry';

    import { LocationApi } from './LocationApi';
    import { api, clientSettings, jobs_filter_config } from '../../../stores/api';
    import { type FilterListType } from '../../../stores/filter';
    import Spinner from '../../shared/Spinner/Spinner.svelte';
    import MessageDanger from '../../shared/Messages/MessageDanger.svelte';
    import { ApiFilterBuilder } from '@automapa/svelte-automapa-template';
    import { onMapReady } from '../../utilities/onMapReady';
    import { JobsOnMap, getJobPin, getJobGroup, getJobLabel, getJobCluster } from './JobsOnMap.js';
    import MessageFoundItems from '../../shared/MessageFoundItems.svelte';
    import SwitchView from '../../shared/SwitchView.svelte';
    import { debug, jobs, simpleView } from '../../../stores/store';
    import ErrorInline from '../../shared/ErrorInline.svelte';
    import { getFilterQuery, resetFilterQuery } from '../../utilities/getFilterQuery';
    import { canWrite } from '../../shared/Permissions/writeAccess';
    import JobListFilter from './JobListFilter.svelte';
    import { Navigation } from '../../utilities/Navigation';
    import { Location } from './Location';
    import { BalloonContentGenerator } from '../../shared/BalloonContentGenerator';
    import { getJobColor } from '../../utilities/getJobColor';
    import { features } from '../../shared/Features/features';
    import JobOneRow2 from './RowDesign2/JobOneRow2.svelte';
    import JobOneHeader2 from './RowDesign2/JobOneHeader2.svelte';
    import { SettingsApi } from '../Settings/SettingsApi';
    import { getItemsFromLocationTypes } from '../../utilities/getItemsFromLocationTypes';

    export let showMonit: boolean = false;
    export let onClick: ((location: Location) => void) | null = null;
    export let showFilter: boolean = true;
    export let filter: FilterListType = {};
    export let onChangeFilter: (filter: FilterListType) => void;

    let loading: boolean = false;
    let loadingAfterScroll: boolean = false;
    let error: string = '';
    // let filteredJobs: JobResponseType[] = [];
    let errorSave: string = '';

    const initialLimit = 20;
    let jobFilter: ApiFilterBuilder = new ApiFilterBuilder(initialLimit);
    let limit: number = initialLimit;
    let offset: number = 0;
    let hasMore = true;
    let reloadMap: boolean = true;

    let expandedIndex: number = -1;
    let lastExpandedJobId: number | null = null;
    let letEditAllFiltered: boolean = false;
    let onlyLowQualityAddresses: boolean = false;
    let selectedTargetDate: string = new Date().toISOString().substring(0, 10);
    let zoomBestFitTimeout: any = null;

    let componentMounted: boolean = false;
    let locationTypesItems: { id: string; label: string }[] = [];

    function reloadElements() {
        $jobs.selectedOrUnselected = [];
        $jobs.selectMode = 'selected';
        if (componentMounted && jobsOnMap) {
            jobMapObjects = {};
            try {
                jobsOnMap.setFilter(getFilterQuery(filter));
                if (zoomBestFitTimeout) {
                    clearTimeout(zoomBestFitTimeout);
                }
                zoomBestFitTimeout = setTimeout(() => {
                    jobsOnMap.zoomBestFit(true);
                }, 250);
            } catch (err) {
                console.error('reloadElements', err);
                resetFilterQuery();
            }
        }
        Promise.all([
            LocationApi.getWithQueries($api, getFilterQuery(filter), jobFilter),
            LocationApi.getCountWithQueries($api, getFilterQuery(filter), jobFilter),
            LocationApi.getCountWithQueries($api, {}, jobFilter),
        ])
            .then(response => {
                $jobs.filteredJobs = response[0];
                hasMore = response[0].length !== 0;
                offset = 0;
                $jobs.countFiltered = response[1];
                $jobs.countTotal = response[2];
                expandedIndex = -1;
                letEditAllFiltered = Object.keys(getFilterQuery(filter)).length > 0 && $jobs.filteredJobs.length > 0 && !hasMore;
            })
            .catch(err => {
                error = err;
                console.error('JobList.reloadElements', err);
                resetFilterQuery();
            });
    }

    function onFilterChange() {
        if (offset + limit > $jobs.filteredJobs.length) {
            offset = 0;
        }
        $jobs.selectedOrUnselected = [];
        $jobs.selectMode = 'selected';
        jobFilter = jobFilter;
        expandedIndex = -1;
        reloadElements();
    }

    // Will be called when we've scrolled to the pont where more data needs to be fetched
    const handleChange = (e: any) => {
        if (e.detail.inView && hasMore) {
            loadingAfterScroll = true;
            offset = offset + limit;
            jobFilter.paginate(limit, 0);
            let tempJobFilter = Object.assign(Object.create(Object.getPrototypeOf(jobFilter)), jobFilter);
            LocationApi.getWithQueries($api, getFilterQuery(filter), tempJobFilter.paginate(limit, offset))
                .then(response => {
                    $jobs.filteredJobs = [...$jobs.filteredJobs];
                    // $jobs.selectAllChecked = false;
                    response.forEach(job => {
                        if (!$jobs.filteredJobs.find(filteredJob => filteredJob.id === job.id)) {
                            $jobs.filteredJobs.push(job);
                        }
                    });
                    hasMore = response.length !== 0;
                    letEditAllFiltered = Object.keys(getFilterQuery(filter)).length > 0 && $jobs.filteredJobs.length > 0 && !hasMore;
                    // $jobs.visibleJobs = [...filteredJobs.map( (job) => job.id)];
                })
                .catch(err => {
                    console.error('JobList.handleChange', err);
                })
                .finally(() => {
                    loadingAfterScroll = false;
                });
        }
    };

    let onJobCoordsSet = function (id: number | null | undefined) {
        if (typeof id == 'number') {
            if (jobMapObjects && id in jobMapObjects) {
                delete jobMapObjects[id];
            }
            jobsOnMap.jobReloadSingle(id);
        } else {
            jobMapObjects = {};
            jobsOnMap.jobsReload();
        }
    };

    // let clickedJob: any;
    let jobsOnMap: JobsOnMap;
    let jobMapObjects: any = {};

    const getJobMapObject = (job: any) => {
        let mapObj;
        let zIndex = 502 + 90 - job.geom.coordinates[1]; // Make icons place like for real
        if (job.count && job.count > 1) {
            mapObj = new Targeo.MapObject.Point(
                { x: job.geom.coordinates[0], y: job.geom.coordinates[1] },
                getJobGroup('#ffffff', 30 + (job.count.toString().length - 1) * 10, {
                    total: job.count,
                    filtered: job.filter,
                })
            );
        } else {
            let coordkey = JSON.stringify(job.geom.coordinates);
            if (!(job.id in jobMapObjects) || jobMapObjects[job.id].coords != coordkey) {
                let color = getJobColor(new Location().set(job));
                mapObj = new Targeo.MapObject.Point(
                    { x: job.geom.coordinates[0], y: job.geom.coordinates[1] },
                    getJobPin(
                        color,
                        job.filter === false
                            ? JobsOnMap.pinSizes.unmatching
                            : job.filter === true
                              ? JobsOnMap.pinSizes.matching
                              : JobsOnMap.pinSizes.normal,
                        { type: job.doc },
                        $clientSettings.display.icon_style
                    ),
                    'job_' + job.id,
                    {
                        name: getJobLabel(job),
                        cluster: getJobCluster(
                            color,
                            job.filter === false
                                ? JobsOnMap.pinSizes.unmatching
                                : job.filter === true
                                  ? JobsOnMap.pinSizes.matching
                                  : JobsOnMap.pinSizes.normal,
                            { type: job.doc },
                            $clientSettings.display.icon_style
                        ),
                        zIndex: zIndex,
                        balloon: {
                            body: '<div class="lds-dual-ring-improved" style="width: 60px; height: 60px;"></div>',
                        },
                        mouseButtonActions: {
                            left: function (mapObj: any) {
                                let jobId = parseInt(mapObj.id.slice(4));
                                console.log('JobList, point on map clicked', mapObj.id, jobId);
                                if (expandedIndex === jobId) {
                                    closeJob(jobId);
                                } else {
                                    openJob(jobId);
                                }
                                // $jobs = $jobs;
                                // JobApi.getOne($api, jobId)
                                //     .then(job => {
                                //         clickedJob = { job: job };
                                //     })
                                //     .catch(err => {
                                //         error = err;
                                //         resetFilterQuery();
                                //     });
                            },
                        },
                    }
                );
                jobMapObjects[job.id] = {
                    coords: coordkey,
                    mapObject: mapObj,
                    detailsLoaded: false,
                    balloonListener: null,
                    color: color,
                    pinOpts: { type: job.doc },
                };
            } else {
                mapObj = jobMapObjects[job.id].mapObject;
            }
            if (!jobMapObjects[job.id].detailsLoaded) {
                if (jobMapObjects[job.id].balloonListener) {
                    jobMapObjects[job.id].mapObject.unlistenByKey(jobMapObjects[job.id].balloonListener);
                }
                jobMapObjects[job.id].balloonListener = mapObj.listenOnce('onBalloonShow', function (ev: any) {
                    jobMapObjects[job.id].balloonListener = null;
                    var jobId = parseInt(ev.target.id.slice(4));
                    LocationApi.getOne($api, jobId)
                        .then(job => {
                            if (jobMapObjects && jobId in jobMapObjects) {
                                let html = new BalloonContentGenerator(new Location().set(job)).getBalloonHtmlArray();
                                jobMapObjects[job.id].mapObject.setBalloonOptions({
                                    body: html.join(''),
                                });
                                Targeo.Base.Map.getObjectManager().refreshBalloon('job_' + job.id);
                                jobMapObjects[job.id].detailsLoaded = true;
                            } else {
                                console.warn('JobList.getJobMapObject: balloon show for job not in jobMapObjects');
                            }
                        })
                        .catch(err => {
                            error = err;
                            console.error('JobList.getJobMapObject', err);
                            resetFilterQuery();
                        });
                });
            }
        }
        return mapObj;
    };

    const emphasiseJobOnMap = (jobId: number, emphasise: boolean) => {
        if (jobMapObjects[jobId] && jobMapObjects[jobId].mapObject) {
            jobMapObjects[jobId].mapObject.setIcon(
                getJobPin(
                    jobMapObjects[jobId].color,
                    emphasise ? JobsOnMap.pinSizes.matching : JobsOnMap.pinSizes.normal,
                    jobMapObjects[jobId].pinOpts,
                    $clientSettings.display.icon_style
                )
            );
        }
    };

    function openJob(jobId: number) {
        if (lastExpandedJobId !== null) {
            emphasiseJobOnMap(lastExpandedJobId, false);
        }
        lastExpandedJobId = jobId;
        emphasiseJobOnMap(jobId, true);
        if (!$jobs.filteredJobs.find(job => job.id === jobId)) {
            //. load job in not loaded in the list (dynamically)
            LocationApi.getOne($api, jobId)
                .then(job => {
                    $jobs.filteredJobs = [...$jobs.filteredJobs, job];
                    expandedIndex = jobId; // toggle job to open after loaded
                    // scrollToJob(jobId);
                    setTimeout(() => {
                        scrollToJob(jobId);
                    }, 50);
                })
                .catch(err => {
                    error = err;
                    console.error('JobList.openJob', err);
                });
        } else {
            expandedIndex = jobId; // toggle job to open if already loaded
            setTimeout(() => {
                scrollToJob(jobId);
            }, 50);
        }
    }

    function closeJob(jobId: number) {
        expandedIndex = -1;
        emphasiseJobOnMap(jobId, false);
    }

    function scrollToJob(jobId: number) {
        const jobElement = document.getElementById(`job-${jobId}`);
        if (jobElement) {
            jobElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }

    onMount(() => {
        componentMounted = true;
        Promise.all([
            onMapReady(),
            LocationApi.getLocationTypes(get(api)),
            // SettingsApi.getFilterConfig($api, 'location'),
        ])
            .then(response => {
                locationTypesItems = getItemsFromLocationTypes(response[1]);
                // $jobs_filter_config = response[2];
                if (componentMounted) {
                    jobsOnMap = new JobsOnMap({
                        getMapObject: getJobMapObject,
                        projection: ['id', 'geom', 'location_type'],
                        filter: getFilterQuery(filter),
                        maxPointZoom: 32,
                        // klikniecie w mape prowadzi do strony dodawania punktu, wiec dla konta
                        // read-only nie wlaczamy tego trybu
                        addJob: get(canWrite),
                    });
                    jobsOnMap.addEventListener('jobadd', (evt: any) => {
                        console.log('!!! addJob event', evt.detail);
                        $jobs.revGeoDetails = evt.detail;
                        Navigation.navigate('/manual-add-job/');
                    });
                    jobsOnMap.zoomBestFit(true);
                }
            })
            .catch(err => {
                error = err;
                console.error('JobList.onMount', err);
                resetFilterQuery();
            });

        return () => {
            componentMounted = false;
            if (jobsOnMap) {
                jobMapObjects = {};
                jobsOnMap.destroy();
            } else {
                // captureMessage('captureMessage: JobsList.onMount: jobsOnMap not ready');
            }
        };
    });

    $: {
        if (!error) {
            // $filter.jobsToBeCompleted.queries = $filter.jobsToBeCompleted.queries;
            onChangeFilter(filter);
            reloadElements();
        }
    }

    $: {
        if ($jobs.forceRecount) {
            $jobs.forceRecount = false;
            $jobs.selectedOrUnselected = [];
            $jobs.selectMode = 'selected';
            Promise.all([
                LocationApi.getCountWithQueries($api, getFilterQuery(filter), jobFilter),
                LocationApi.getCountWithQueries($api, {}, jobFilter),
            ])
                .then(response => {
                    $jobs.countFiltered = response[0];
                    $jobs.countTotal = response[1];
                })
                .catch(err => {
                    error = err;
                    console.error('JobList.$jobs.forceRecount', err);
                    resetFilterQuery();
                });
        }
    }

    $: {
        if ($jobs.forceReload) {
            $jobs.forceReload = false;
            $jobs.selectedOrUnselected = [];
            $jobs.selectMode = 'selected';
            reloadElements();
            if (error) {
                resetFilterQuery();
            }
        }
    }
</script>

{#if error}
    <MessageDanger message={$_('Failed to fetch list items')} bind:error />
{/if}
{#if errorSave}
    <ErrorInline text={errorSave} />
{/if}

{#if showFilter}
    <div style="margin-top: 10px;" class:blur={showMonit}>
        <JobListFilter makeBlured={false} filteredCount={$jobs.countFiltered.count} totalCount={$jobs.countTotal.count} />
    </div>
{/if}
{#if loading}
    <div in:blur={{ delay: 500, duration: 1000 }}>
        <Spinner />
    </div>
{:else}
    {#if $features.feature_location_list_design_v1}
        {#if $jobs.countTotal.count > 0}
            <div style="flex:0; justify-content: flex-end; display: flex;">
                <span style="margin-left: auto;">
                    <SwitchView bind:checked={$simpleView} />
                </span>
            </div>
        {/if}
    {/if}

    {#if $jobs.countTotal.count > 0}
        <JobOneHeader2 bind:jobFilter {onFilterChange} bind:simpleView={$simpleView} />
    {/if}

    <div class="job-list">
        {#each $jobs.filteredJobs as job, index (job.id)}
            <div in:blur|global id={`job-${job.id}`}>
                <JobOneRow2
                    bind:job
                    index={job.id}
                    bind:expandedIndex
                    showHeader={false}
                    {jobsOnMap}
                    bind:simpleView={$simpleView}
                    on:setcoordinates={() => onJobCoordsSet(job.id)}
                    on:open={() => {
                        console.log('JobOneRow on:open');
                        if (typeof onClick === 'function') {
                            onClick(new Location().set(job));
                        } else {
                            openJob(job.id);
                        }
                    }}
                    on:close={() => {
                        console.log('JobOneRow on:close');
                        closeJob(job.id);
                        //clickedJob = null;
                    }}
                    {locationTypesItems}
                />
            </div>
        {/each}

        {#if loadingAfterScroll}
            <Spinner />
        {:else if hasMore}
            <div
                class="inview-element"
                use:inview={{}}
                on:change={e => {
                    handleChange(e);
                }}
            />
        {:else}
            <MessageFoundItems count={$jobs.filteredJobs.length} />
        {/if}
    </div>
{/if}

<style>
    .inview-element {
        height: 100px;
    }

    .job-list {
        display: flex;
        flex-direction: column;
        overflow-y: auto;
        overflow-x: hidden;
        padding-bottom: 40px;
        padding-right: 12px;
        margin-bottom: 20px;
        width: 100%;
    }
</style>
